/**
 * AYM ecommerce analytics — PostHog event tracking for both AYM stores.
 *
 * Emits PostHog's ecommerce event spec (Segment-shaped: "Product Viewed",
 * "Product Added", …) so insights, funnels and CDP destinations work without
 * remapping. See docs/analytics.md for the full tracking plan, the property
 * dictionary and the PostHog insight recipes built on these events.
 *
 * Data sources:
 *  - window.AYMAnalytics    server-rendered page context (snippets/analytics-data.liquid)
 *  - <product-card> JSON    per-card payloads for list impressions/clicks
 *                           (snippets/product-card.liquid + product-card-color.liquid)
 *  - Theme custom events    variant:add, cart:change, variant:change, facet:update,
 *                           dialog:after-show — dispatched by the Stretch bundle
 *  - Birl embed events      birl:added (embed-managed add-to-cart, defensive)
 *
 * Checkout: theme JS cannot run on Shopify checkout pages, so this module
 * only captures 'Checkout Started' on the checkout button click (dynamic
 * payment buttons bypass it). Everything inside checkout (steps, purchases)
 * is Shopify analytics territory for now; the optional custom web pixel in
 * docs/posthog-checkout-pixel.js adds it to PostHog if ever wanted.
 *
 * window.posthog is installed by the <birl-analytics> element (Birl embed,
 * loaded async) and is consent-gated there — captures before consent are
 * dropped by PostHog itself, so this module never has to check consent.
 */

const DATA = window.AYMAnalytics || {};

/* ------------------------------------------------------------------ */
/* Capture plumbing: buffer until the embed has installed the posthog  */
/* stub, then flush. The stub itself queues until PostHog's array.js   */
/* arrives, so handing events to the stub is enough.                   */
/* ------------------------------------------------------------------ */

let adopted = false;
let pending = [];

function capture(event, props) {
  const payload = { page_type: DATA.pageType || null, ...props };
  // Always read the LIVE global. The embed first installs a queueing stub and
  // later swaps in the real PostHog; a captured reference keeps feeding the
  // orphaned stub, whose queue was already drained — page-load events won that
  // race and arrived, every later interaction event (Product Added, Cart
  // Viewed, Checkout Started) silently vanished into the dead stub.
  const ph = window.posthog;
  if (ph && typeof ph.capture === 'function') {
    ph.capture(event, payload);
  } else if (pending) {
    pending.push([event, payload]);
  }
}

function adoptPosthog() {
  if (adopted || !window.posthog) return;
  adopted = true;
  const queued = pending || [];
  pending = null;
  queued.forEach(([event, payload]) => capture(event, payload));
}

document.addEventListener('birl:analytics-ready', adoptPosthog);
(function pollForPosthog(started) {
  adoptPosthog();
  if (adopted) return;
  if (Date.now() - started > 30000) {
    pending = null; // analytics never booted (no key / blocked) — stop buffering
    return;
  }
  setTimeout(() => pollForPosthog(started), 250);
})(Date.now());

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const money = (cents) => (typeof cents === 'number' ? Math.round(cents) / 100 : undefined);

function compact(obj) {
  const out = {};
  for (const key of Object.keys(obj)) {
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== '') out[key] = obj[key];
  }
  return out;
}

/** Map a Shopify /cart.js (or /cart/add.js) line item to spec product props. */
function lineItemProps(item) {
  return compact({
    product_id: String(item.product_id),
    variant_id: String(item.variant_id || item.id),
    sku: item.sku,
    name: item.product_title || item.title,
    brand: item.vendor,
    category: item.product_type,
    variant: item.variant_title,
    price: money(item.final_price != null ? item.final_price : item.price),
    quantity: item.quantity,
    url: item.url ? (DATA.origin || '') + item.url : undefined,
    image_url: item.image || undefined,
    currency: DATA.currency,
  });
}

/** Flat product props for the current PDP (from the Liquid data layer). */
function pdpProductProps() {
  const p = DATA.product;
  if (!p) return null;
  return compact({
    product_id: p.product_id,
    sku: p.sku,
    name: p.name,
    brand: p.brand,
    category: p.category,
    variant: p.variant,
    price: p.price,
    currency: DATA.currency,
    url: p.url,
    image_url: p.image_url,
    compare_at_price: p.compare_at_price,
    discount_pct:
      p.compare_at_price && p.price && p.compare_at_price > p.price
        ? Math.round((1 - p.price / p.compare_at_price) * 100)
        : undefined,
    available: p.available,
    condition: p.condition,
    grade: p.grade,
    is_renewed: p.is_renewed,
    listing_age_days: listingAgeDays(p.published_at),
    listed_at: p.published_at,
  });
}

function listingAgeDays(publishedAt) {
  if (!publishedAt) return undefined;
  const ts = Date.parse(publishedAt);
  if (Number.isNaN(ts)) return undefined;
  return Math.max(0, Math.floor((Date.now() - ts) / 86400000));
}

/* ------------------------------------------------------------------ */
/* Cart state: seeded server-side, kept fresh from theme cart events.  */
/* Diffing lastCart -> nextCart turns any cart mutation into           */
/* Product Added / Product Removed without double counting.            */
/* ------------------------------------------------------------------ */

let lastCart = DATA.cart || null;

function snapshotFromCartJs(cart) {
  return {
    cart_id: cart.token,
    value: money(cart.total_price),
    item_count: cart.item_count,
    items: (cart.items || []).map(lineItemProps),
  };
}

function diffCarts(prev, next) {
  // Aggregate BOTH sides per variant (a variant can span several cart lines —
  // line-item properties, selling plans — and /cart.js keys lines by `key`).
  const aggregate = (items) => {
    const map = new Map();
    (items || []).forEach((item) => {
      const existing = map.get(item.variant_id);
      if (existing) existing.quantity += item.quantity;
      else map.set(item.variant_id, { ...item });
    });
    return map;
  };
  const prevMap = aggregate(prev?.items);
  const nextMap = aggregate(next.items);
  const added = [];
  const removed = [];
  nextMap.forEach((item, key) => {
    const before = prevMap.get(key);
    const delta = item.quantity - (before ? before.quantity : 0);
    if (delta > 0) added.push({ ...item, quantity: delta });
    else if (delta < 0) removed.push({ ...item, quantity: -delta });
  });
  prevMap.forEach((item, key) => {
    if (!nextMap.has(key)) removed.push({ ...item });
  });
  return { added, removed };
}

let cartGeneration = 0;

function trackCartMutation(cartJs, baseEvent) {
  cartGeneration += 1;
  const next = snapshotFromCartJs(cartJs);
  const { added, removed } = diffCarts(lastCart, next);
  lastCart = next;
  const shared = { cart_id: next.cart_id, cart_value: next.value, source: baseEvent };
  added.forEach((item) => capture('Product Added', { ...item, ...shared }));
  removed.forEach((item) => capture('Product Removed', { ...item, ...shared }));
}

document.addEventListener('cart:change', (event) => {
  const detail = event.detail || {};
  if (detail.cart) trackCartMutation(detail.cart, detail.baseEvent || 'cart:change');
});

// Defensive: embed-managed add-to-cart (Birl resale/p2p flows). Inert on AYM
// today — birl-pdp runs in link-out mode — but if a native p2p add ever lands
// here it mutates /cart.js without a theme cart:change, so refetch and diff.
// The generation check drops the response if a theme mutation (which carries
// a fresher cart) landed while the fetch was in flight.
document.addEventListener('birl:added', () => {
  const generation = cartGeneration;
  fetch('/cart.js')
    .then((res) => (res.ok ? res.json() : null))
    .then((cart) => {
      if (cart && cartGeneration === generation) trackCartMutation(cart, 'birl:added');
    })
    .catch(() => {});
});

// bfcache restore: the JS heap (incl. lastCart) is revived as-is, but the cart
// may have changed on other pages. Resync silently — those mutations were
// already tracked where they happened.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  fetch('/cart.js')
    .then((res) => (res.ok ? res.json() : null))
    .then((cart) => {
      if (cart) lastCart = snapshotFromCartJs(cart);
    })
    .catch(() => {});
});

// Cart PAGE mutations reload the page instead of dispatching cart:change
// (theme.js reloads after /cart/change.js when pageType === 'cart'). Stash the
// pre-mutation cart when the theme announces the change (line-item:will-change
// fires before the POST), and diff it against the fresh server-rendered seed
// on the next cart-page load.
const CART_STASH_KEY = 'aym:analytics:cart-page-stash';
if (DATA.pageType === 'cart') {
  const stashCart = () => {
    try {
      sessionStorage.setItem(
        CART_STASH_KEY,
        JSON.stringify({ at: Date.now(), cart: lastCart })
      );
    } catch (error) {
      /* storage unavailable */
    }
  };
  document.addEventListener('line-item:will-change', stashCart);
  // Adds initiated FROM the cart page redirect to /cart without events too.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (form && form.getAttribute && /\/cart\/add/.test(form.getAttribute('action') || '')) {
        stashCart();
      }
    },
    true
  );
  try {
    const raw = sessionStorage.getItem(CART_STASH_KEY);
    if (raw) {
      sessionStorage.removeItem(CART_STASH_KEY);
      const stash = JSON.parse(raw);
      if (
        stash &&
        stash.cart &&
        Date.now() - stash.at < 60000 &&
        stash.cart.cart_id === (lastCart && lastCart.cart_id)
      ) {
        const { added, removed } = diffCarts(stash.cart, lastCart);
        const shared = {
          cart_id: lastCart.cart_id,
          cart_value: lastCart.value,
          source: 'cart-page-reload',
        };
        added.forEach((item) => capture('Product Added', { ...item, ...shared }));
        removed.forEach((item) => capture('Product Removed', { ...item, ...shared }));
      }
    }
  } catch (error) {
    /* storage unavailable or corrupt stash — skip */
  }
}

/* ------------------------------------------------------------------ */
/* Cart Viewed: drawer open (incl. auto-open after add) + cart page.   */
/* dialog:after-show does not bubble — use a capture-phase listener.   */
/* ------------------------------------------------------------------ */

function cartViewedProps() {
  const cart = lastCart || { cart_id: undefined, value: 0, item_count: 0, items: [] };
  return {
    cart_id: cart.cart_id,
    value: cart.value,
    currency: DATA.currency,
    // Total unit quantity (Shopify cart.item_count semantics), not line count.
    item_count: cart.item_count,
    products: cart.items,
  };
}

document.addEventListener(
  'dialog:after-show',
  (event) => {
    if (event.target && event.target.id === 'cart-drawer') {
      capture('Cart Viewed', { ...cartViewedProps(), source: 'drawer' });
    }
  },
  true
);

if (DATA.pageType === 'cart') {
  capture('Cart Viewed', { ...cartViewedProps(), source: 'page' });
}

// Checkout Started: click on the checkout submit button (drawer + cart page).
// An approximation — dynamic payment buttons (Shop Pay etc.) bypass this —
// but good enough while the checkout pixel is not installed; if the pixel
// (docs/posthog-checkout-pixel.js) is adopted later, its events carry
// $lib: 'aym-checkout-pixel' so the two sources stay distinguishable.
document.addEventListener(
  'click',
  (event) => {
    const button = event.target.closest && event.target.closest('button[name="checkout"]');
    if (!button) return;
    capture('Checkout Started', { ...cartViewedProps(), source: 'storefront_click' });
  },
  true
);

/* ------------------------------------------------------------------ */
/* PDP: Product Viewed + variant selection (size/colour demand signal) */
/* ------------------------------------------------------------------ */

if (DATA.product) {
  capture(
    'Product Viewed',
    compact({
      ...pdpProductProps(),
      value: DATA.product.price,
      // Renewed demand backlog: views of already-sold one-of-one listings.
      sold_out_view: DATA.product.available === false ? true : undefined,
    })
  );
}

document.addEventListener('variant:change', (event) => {
  const detail = event.detail || {};
  const variant = detail.variant;
  // variant:change bubbles from EVERY picker on the page (quick-buy modals,
  // cross-sell) — only attach the PDP product props when the dispatching
  // picker actually belongs to the PDP product. The event is dispatched on
  // the product FORM; note detail.formId is unusable (the form's `id`
  // property is shadowed by its <input name="id"> child).
  const form = event.target;
  const formId = form && form.getAttribute ? form.getAttribute('id') : null;
  let handle = null;
  if (formId && window.CSS && CSS.escape) {
    const picker = document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`);
    if (picker) handle = picker.getAttribute('handle');
  }
  let isPdpPicker = false;
  if (DATA.product) {
    if (handle) isPdpPicker = handle === DATA.product.handle;
    else if (formId) isPdpPicker = formId.indexOf(`-${DATA.product.product_id}-`) !== -1;
    else isPdpPicker = true;
  }
  const base = isPdpPicker ? pdpProductProps() || {} : {};
  capture(
    'Product Variant Selected',
    compact({
      ...base,
      product_handle: handle || (isPdpPicker && DATA.product ? DATA.product.handle : undefined),
      variant_id: variant ? String(variant.id) : undefined,
      variant: variant ? variant.title : undefined,
      sku: variant ? variant.sku : base.sku,
      price: variant ? money(variant.price) : base.price,
      available: variant ? !!variant.available : false,
      unavailable_combination: !variant,
    })
  );
});

/* ------------------------------------------------------------------ */
/* Product list impressions + clicks.                                  */
/* Cards carry a JSON payload; impressions fire when a card is >= 50%  */
/* visible (industry-standard viewport rule), batched per list, deduped */
/* per page. facet:update re-renders swap cards — the MutationObserver */
/* picks the new ones up automatically.                                */
/* ------------------------------------------------------------------ */

const seenImpressions = new Set();
let impressionBuffer = [];
let impressionFlushTimer = null;

function cardPayload(card) {
  if (card.__aymPayload !== undefined) return card.__aymPayload;
  let payload = null;
  const script = card.querySelector('script[data-aym-analytics]');
  if (script) {
    try {
      payload = JSON.parse(script.textContent);
    } catch (error) {
      payload = null;
    }
  }
  card.__aymPayload = payload;
  return payload;
}

function listIdFor(card) {
  const section = card.closest('.shopify-section');
  let listId = section ? section.id.replace(/^shopify-section-/, '') : 'unknown';
  const splitAt = listId.indexOf('__');
  if (splitAt !== -1) listId = listId.slice(splitAt + 2);
  return listId;
}

// Event-level list category = the collection being browsed. Product-level
// `category` (the product type) stays untouched on each item.
const listCategory = () => (DATA.collection ? DATA.collection.handle : undefined);

function impressionProps(card) {
  const payload = cardPayload(card);
  if (!payload) return null;
  return compact({
    ...payload,
    list_id: listIdFor(card),
    currency: DATA.currency,
    listing_age_days: listingAgeDays(payload.listed_at),
  });
}

function flushImpressions() {
  impressionFlushTimer = null;
  if (!impressionBuffer.length) return;
  const byList = new Map();
  impressionBuffer.forEach((item) => {
    const key = item.list_id || 'unknown';
    if (!byList.has(key)) byList.set(key, []);
    byList.get(key).push(item);
  });
  impressionBuffer = [];
  byList.forEach((products, listId) => {
    capture(
      'Product List Viewed',
      compact({
        list_id: listId,
        category: listCategory(),
        currency: DATA.currency,
        products,
      })
    );
  });
}

const impressionObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            impressionObserver.unobserve(entry.target);
            const props = impressionProps(entry.target);
            if (!props) return;
            const dedupeKey = `${props.list_id}:${props.product_id}:${props.variant || ''}`;
            if (seenImpressions.has(dedupeKey)) return;
            seenImpressions.add(dedupeKey);
            impressionBuffer.push(props);
            if (!impressionFlushTimer) impressionFlushTimer = setTimeout(flushImpressions, 1000);
          });
        },
        { threshold: 0.5 }
      )
    : null;

function observeCards(root) {
  if (!impressionObserver || !root.querySelectorAll) return;
  root.querySelectorAll('product-card').forEach((card) => {
    if (card.__aymObserved) return;
    card.__aymObserved = true;
    impressionObserver.observe(card);
  });
}

if (impressionObserver) {
  observeCards(document);
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.tagName === 'PRODUCT-CARD' && !node.__aymObserved) {
          node.__aymObserved = true;
          impressionObserver.observe(node);
        } else {
          observeCards(node);
        }
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
}

document.addEventListener('click', (event) => {
  const card = event.target.closest && event.target.closest('product-card');
  if (!card) return;
  const quickBuy = event.target.closest('[aria-controls^="product-quick-buy"]');
  const link = event.target.closest('a[href*="/products/"]');
  if (!quickBuy && !link) return;
  const props = impressionProps(card);
  if (!props) return;
  capture('Product Clicked', { ...props, click_type: quickBuy ? 'quick_buy' : 'card' });
});

/* ------------------------------------------------------------------ */
/* Search: results page (authoritative, has result counts — the        */
/* zero-results report is the highest-ROI search insight) + debounced  */
/* predictive-search queries.                                          */
/* ------------------------------------------------------------------ */

if (DATA.search && DATA.search.performed) {
  capture('Products Searched', {
    query: DATA.search.query,
    results_count: DATA.search.results_count,
    zero_results: DATA.search.results_count === 0,
    source: 'results_page',
  });
}

(function trackPredictiveSearch() {
  let debounceTimer = null;
  let lastQuery = '';
  document.addEventListener('input', (event) => {
    const input = event.target;
    if (!input || input.id !== 'search-drawer-input') return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const query = input.value.trim();
      if (query.length < 3 || query === lastQuery) return;
      lastQuery = query;
      capture('Products Searched', { query, source: 'predictive' });
    }, 1200);
  });
})();

/* ------------------------------------------------------------------ */
/* Collection filtering / sorting                                      */
/* ------------------------------------------------------------------ */

// facet:update also fires for pagination links (they reuse the facet AJAX
// machinery) — only capture when the filter/sort state actually changed.
const facetSignature = (params) =>
  Array.from(params)
    .filter(([key]) => key.startsWith('filter.') || key === 'sort_by')
    .map(([key, value]) => `${key}=${value}`)
    .sort()
    .join('&');

let lastFacetSignature = facetSignature(new URLSearchParams(window.location.search));

document.addEventListener('facet:update', (event) => {
  const url = event.detail && event.detail.url;
  if (!url || !url.searchParams) return;
  const signature = facetSignature(url.searchParams);
  if (signature === lastFacetSignature) return; // pagination / no-op re-render
  lastFacetSignature = signature;
  const filters = [];
  const sorts = [];
  url.searchParams.forEach((value, key) => {
    if (key.startsWith('filter.')) {
      filters.push({ type: key.replace(/^filter\.[pv]\./, ''), value });
    } else if (key === 'sort_by') {
      sorts.push({ type: 'sort_by', value });
    }
  });
  // Same list vocabulary as Product List Viewed: the filtered list is the
  // page's main product list.
  capture(
    'Product List Filtered',
    compact({
      list_id: 'main',
      category: listCategory(),
      filters,
      sorts,
    })
  );
});

/* ------------------------------------------------------------------ */
/* Demand-capture signals                                              */
/* ------------------------------------------------------------------ */

// Klaviyo back-in-stock modal (main store): explicit, named demand for a
// variant that is out of stock — a sourcing/restock signal. The form lives in
// a <dialog class="klaviyo-back-in-stock-dialog"> that is a SIBLING of the
// <klaviyo-back-in-stock> element, so match the dialog/form, not the element.
// (Deliberately NOT form.klaviyo-form — Klaviyo's injected newsletter signups
// carry that class too.)
document.addEventListener(
  'submit',
  (event) => {
    const form =
      event.target &&
      event.target.closest &&
      event.target.closest('.klaviyo-back-in-stock-dialog form, form#klaviyo-back-in-stock');
    if (!form) return;
    const variantInput = form.querySelector('[name="variant_id"]');
    capture(
      'Back In Stock Requested',
      compact({
        ...(pdpProductProps() || {}),
        variant_id: variantInput ? variantInput.value : undefined,
      })
    );
  },
  true
);

// Cross-store bridge: clicks on the "Buy Renewed" / dual-price cross-sell.
// On the main store this is mainline→resale interest (the Birl program's
// acquisition metric); the renewed store's inverse card is caught too.
document.addEventListener('click', (event) => {
  const cta =
    event.target.closest &&
    (event.target.closest('a[data-birl-role="buy-other"]') ||
      event.target.closest('a[data-aym-renewed-cta]'));
  if (!cta) return;
  capture(
    'Renewed Cross Sell Clicked',
    compact({
      ...(pdpProductProps() || {}),
      cta: cta.dataset.aymRenewedCta || 'dual-price',
      target_url: cta.href,
    })
  );
});
